import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DemoExportGate } from "@/components/DemoExportGate";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";
import {
  Send,
  ArrowLeftRight,
  FileText,
  DollarSign,
  MailCheck,
  Link2Off,
  Calendar,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  GitCompare,
  Download,
  BellRing,
  RotateCcw,
  Mail,
  MailOpen,
  MailWarning,
  MailX,
  MousePointerClick,
  Clock,
  RefreshCw,
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion,
  BellOff,
  Bell,
  Sparkles,
  Lock,
  CheckCircle2,
  Inbox,
  Hourglass,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { ReceiveResponseDialog } from "./ReceiveResponseDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface SnapshotClause {
  contractClauseId: number;
  clauseId: number;
  clauseVersionId: number;
  position: number;
  groupLabel?: string | null;
  title: string;
  category: string;
  body: string;
  isOverridden: boolean;
  isLocked?: boolean;
  proposedAlternate?: string | null;
}

interface SnapshotTerms {
  contractType?: string;
  startDate?: string;
  endDate?: string;
  annualSalary?: number | null;
  signingBonus?: number | null;
  buyoutClause?: number | null;
  mitigationRequired?: boolean;
  buyoutSchedule?: { year: number; amount: number }[];
  bonuses?: { id: string; label: string; amount: number; paymentTiming: string }[];
  allowances?: { id: string; label: string; amount: number; paymentTiming: string }[];
}

interface SnapshotData {
  clauseCount?: number;
  annualSalary?: number | null;
  buyoutClause?: number | null;
  startDate?: string;
  endDate?: string;
  contractType?: string;
  capturedAt?: string;
  terms?: SnapshotTerms;
  clauses?: SnapshotClause[];
}

type EmailStatus =
  | "not_sent"
  | "queued"
  | "sent"
  | "delivered"
  | "opened"
  | "clicked"
  | "bounced"
  | "complained"
  | "failed";

// Task #563 — Inline counterparty comments per round.
// Shape matches GET /api/comments?entityType=contract&entityId=:id
// See artifacts/api-server/src/routes/comments.ts:237 for the source-of-truth response.
interface ContractComment {
  id: number;
  authorName: string;
  authorUserId: string | null;
  body: string;
  createdAt: string;
}

interface NegotiationRound {
  id: number;
  contractId: number;
  roundNumber: number;
  direction: "sent" | "received";
  recipientName: string | null;
  recipientEmail: string | null;
  recipientRole: string | null;
  note: string | null;
  coverNote: string | null;
  responseByDate: string | null;
  counterpartyComments: string | null;
  returnedDocumentKey: string | null;
  returnedDocumentFilename: string | null;
  returnedDocumentContentType: string | null;
  returnedDocumentSize: number | null;
  returnedDocumentScanStatus: "clean" | "infected" | "pending" | "error" | null;
  returnedDocumentScanResult: string | null;
  returnedDocumentScannedAt: string | null;
  receivedAt: string | null;
  roundStatus: "sent" | "received";
  counterpartyId: number | null;
  shareTokenExpiresAt: string | null;
  shareTokenRevokedAt: string | null;
  snapshotData: SnapshotData | null;
  remindersSent?: { day3?: string; day0?: string } | null;
  lastManualReminderAt?: string | null;
  manualReminderCount?: number;
  createdByName: string | null;
  createdAt: string;
  emailStatus?: EmailStatus;
  emailSentAt?: string | null;
  emailDeliveredAt?: string | null;
  emailOpenedAt?: string | null;
  emailClickedAt?: string | null;
  emailBouncedAt?: string | null;
  emailLastEventAt?: string | null;
  emailLastError?: string | null;
  emailResendCount?: number;
}

const ROLE_LABELS: Record<string, string> = {
  coach: "Coach",
  agent: "Agent",
  attorney: "Attorney",
  other: "Counterparty",
};

function fmt(n: number | null | undefined): string {
  if (n == null) return "—";
  return `$${n.toLocaleString()}`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Pull a filename out of a `Content-Disposition` header, preferring the
 * RFC 5987 `filename*=UTF-8''…` parameter (handles non-ASCII names) and
 * falling back to the legacy quoted `filename="…"` form.
 */
function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*([^']*)'([^']*)'([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[3].trim());
    } catch {
      // fall through to the quoted-form fallback
    }
  }
  const quoted = /filename\s*=\s*"([^"]+)"/i.exec(header);
  if (quoted) return quoted[1];
  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  if (bare) return bare[1].trim();
  return null;
}

type ScanStatus = "clean" | "infected" | "pending" | "error" | null;

interface ScanStatusDescriptor {
  label: string;
  tone: "success" | "danger" | "warning" | "neutral";
  Icon: typeof ShieldCheck;
  title: string;
}

function describeScanStatus(
  status: ScanStatus,
  scannedAt: string | null,
): ScanStatusDescriptor {
  const when = scannedAt
    ? ` · ${format(new Date(scannedAt), "MMM d, yyyy 'at' h:mm a")}`
    : "";
  switch (status) {
    case "clean":
      return {
        label: "Scanned clean",
        tone: "success",
        Icon: ShieldCheck,
        title: `Malware scan passed${when}.`,
      };
    case "infected":
      return {
        label: "Blocked — malware detected",
        tone: "danger",
        Icon: ShieldAlert,
        title: `Malware scan flagged this file as infected${when}. Download is blocked.`,
      };
    case "pending":
      return {
        label: "Scan pending",
        tone: "warning",
        Icon: ShieldQuestion,
        title: "The malware scan hasn't completed yet. Download is blocked until it does.",
      };
    case "error":
      return {
        label: "Scan failed",
        tone: "warning",
        Icon: ShieldAlert,
        title: `The malware scanner couldn't verify this file${when}. Download is blocked.`,
      };
    default:
      return {
        label: "Not scanned",
        tone: "neutral",
        Icon: ShieldQuestion,
        title: "This file pre-dates malware scanning and was never verified. Download is blocked — ask the counterparty to resend.",
      };
  }
}

const SCAN_STATUS_TONE_CLASS: Record<ScanStatusDescriptor["tone"], string> = {
  success: "bg-green-50 border-green-300 text-green-700",
  danger: "bg-red-50 border-red-300 text-red-700",
  warning: "bg-amber-50 border-amber-300 text-amber-700",
  neutral: "bg-muted/60 border-border text-muted-foreground",
};

function ScanStatusBadge({ round }: { round: NegotiationRound }) {
  const desc = describeScanStatus(
    round.returnedDocumentScanStatus,
    round.returnedDocumentScannedAt,
  );
  return (
    <Badge
      variant="outline"
      className={cn("text-xs flex items-center gap-1", SCAN_STATUS_TONE_CLASS[desc.tone])}
      title={desc.title}
      data-testid={`badge-scan-status-${round.id}`}
    >
      <desc.Icon className="h-3 w-3" />
      <span>{desc.label}</span>
    </Badge>
  );
}

interface EmailStatusDescriptor {
  label: string;
  tone: "neutral" | "info" | "success" | "warning" | "danger";
  Icon: typeof Mail;
}

function describeEmailStatus(status: EmailStatus | undefined): EmailStatusDescriptor {
  switch (status) {
    case "delivered":
      return { label: "Delivered", tone: "info", Icon: MailCheck };
    case "opened":
      return { label: "Opened", tone: "success", Icon: MailOpen };
    case "clicked":
      return { label: "Link clicked", tone: "success", Icon: MousePointerClick };
    case "bounced":
      return { label: "Bounced", tone: "danger", Icon: MailX };
    case "complained":
      return { label: "Marked as spam", tone: "danger", Icon: MailWarning };
    case "failed":
      return { label: "Send failed", tone: "danger", Icon: MailX };
    case "sent":
      return { label: "Sent", tone: "info", Icon: Mail };
    case "queued":
      return { label: "Queued", tone: "neutral", Icon: Clock };
    case "not_sent":
    default:
      return { label: "Not sent", tone: "neutral", Icon: Mail };
  }
}

const EMAIL_STATUS_TONE_CLASS: Record<EmailStatusDescriptor["tone"], string> = {
  neutral: "bg-muted/60 border-border text-muted-foreground",
  info: "bg-blue-50 border-blue-300 text-blue-700",
  success: "bg-green-50 border-green-300 text-green-700",
  warning: "bg-amber-50 border-amber-300 text-amber-700",
  danger: "bg-red-50 border-red-300 text-red-700",
};

function EmailStatusBadge({ round }: { round: NegotiationRound }) {
  const desc = describeEmailStatus(round.emailStatus);
  const lastEventAt = round.emailLastEventAt ?? round.emailSentAt ?? null;
  const title = lastEventAt
    ? `${desc.label} · last update ${format(new Date(lastEventAt), "MMM d, yyyy 'at' h:mm a")}`
    : desc.label;
  return (
    <Badge
      variant="outline"
      className={cn("text-xs flex items-center gap-1", EMAIL_STATUS_TONE_CLASS[desc.tone])}
      title={title}
      data-testid={`badge-email-status-${round.id}`}
    >
      <desc.Icon className="h-3 w-3" />
      <span>Email: {desc.label}</span>
      {(round.emailResendCount ?? 0) > 0 && (
        <span className="opacity-70">· resent {round.emailResendCount}×</span>
      )}
    </Badge>
  );
}

// "Resend" should be available when the email failed outright OR when it's
// been a while since the recipient opened it.
function shouldOfferResend(round: NegotiationRound): boolean {
  if (round.direction !== "sent") return false;
  if (round.shareTokenRevokedAt) return false;
  if (round.roundStatus === "received") return false;
  const status = round.emailStatus ?? "not_sent";
  return status === "failed" || status === "bounced" || status === "sent" || status === "delivered" || status === "queued" || status === "not_sent";
}

type ClauseDiffEntry =
  | { kind: "added"; current: SnapshotClause }
  | { kind: "removed"; previous: SnapshotClause }
  | { kind: "changed"; current: SnapshotClause; previous: SnapshotClause }
  | { kind: "unchanged"; current: SnapshotClause };

function diffClauses(
  current: SnapshotClause[] | undefined,
  previous: SnapshotClause[] | undefined,
): ClauseDiffEntry[] {
  const cur = current ?? [];
  const prev = previous ?? [];
  const prevById = new Map(prev.map((c) => [c.clauseId, c]));
  const curById = new Map(cur.map((c) => [c.clauseId, c]));
  const entries: ClauseDiffEntry[] = [];

  for (const c of cur) {
    const p = prevById.get(c.clauseId);
    if (!p) entries.push({ kind: "added", current: c });
    else if (p.body !== c.body) entries.push({ kind: "changed", current: c, previous: p });
    else entries.push({ kind: "unchanged", current: c });
  }
  for (const p of prev) {
    if (!curById.has(p.clauseId)) entries.push({ kind: "removed", previous: p });
  }
  return entries;
}

function LineDiff({ before, after }: { before: string; after: string }) {
  const beforeLines = before.split(/\r?\n/);
  const afterLines = after.split(/\r?\n/);
  const beforeSet = new Set(beforeLines);
  const afterSet = new Set(afterLines);
  return (
    <div className="font-mono text-xs leading-relaxed border rounded-md overflow-hidden">
      {beforeLines.map((line, i) => (
        !afterSet.has(line) ? (
          <div key={`b-${i}`} className="bg-red-50 text-red-900 px-2 py-0.5 whitespace-pre-wrap">
            <span className="select-none mr-2 text-red-400">-</span>{line || " "}
          </div>
        ) : null
      ))}
      {afterLines.map((line, i) => (
        !beforeSet.has(line) ? (
          <div key={`a-${i}`} className="bg-green-50 text-green-900 px-2 py-0.5 whitespace-pre-wrap">
            <span className="select-none mr-2 text-green-500">+</span>{line || " "}
          </div>
        ) : (
          <div key={`u-${i}`} className="text-muted-foreground px-2 py-0.5 whitespace-pre-wrap">
            <span className="select-none mr-2"> </span>{line || " "}
          </div>
        )
      ))}
    </div>
  );
}

function TermsBlock({ terms }: { terms: SnapshotTerms }) {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
      <div><span className="text-muted-foreground">Type:</span> {terms.contractType ?? "—"}</div>
      <div><span className="text-muted-foreground">Mitigation:</span> {terms.mitigationRequired ? "Required" : "Not required"}</div>
      <div><span className="text-muted-foreground">Start:</span> {terms.startDate ?? "—"}</div>
      <div><span className="text-muted-foreground">End:</span> {terms.endDate ?? "—"}</div>
      <div><span className="text-muted-foreground">Annual salary:</span> {fmt(terms.annualSalary)}</div>
      <div><span className="text-muted-foreground">Signing bonus:</span> {fmt(terms.signingBonus)}</div>
      <div><span className="text-muted-foreground">Buyout:</span> {fmt(terms.buyoutClause)}</div>
      <div className="col-span-2">
        <span className="text-muted-foreground">Bonuses:</span>{" "}
        {(terms.bonuses ?? []).length === 0
          ? "—"
          : terms.bonuses!.map((b) => `${b.label} (${fmt(b.amount)})`).join(", ")}
      </div>
      <div className="col-span-2">
        <span className="text-muted-foreground">Allowances:</span>{" "}
        {(terms.allowances ?? []).length === 0
          ? "—"
          : terms.allowances!.map((a) => `${a.label} (${fmt(a.amount)})`).join(", ")}
      </div>
    </div>
  );
}

function ClauseListBlock({ clauses }: { clauses: SnapshotClause[] }) {
  if (clauses.length === 0) {
    return <p className="text-xs italic text-muted-foreground">No clauses attached at this round.</p>;
  }
  return (
    <div className="space-y-3">
      {clauses.map((c) => (
        <div key={c.contractClauseId} className="border rounded-md p-3 bg-background">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="text-sm font-medium">{c.title}</div>
            <div className="flex gap-1">
              <Badge variant="outline" className="text-xs">{c.category}</Badge>
              {c.isOverridden && <Badge variant="secondary" className="text-xs">Overridden</Badge>}
              {c.isLocked && <Badge className="text-xs">Locked</Badge>}
            </div>
          </div>
          <pre className="text-xs whitespace-pre-wrap font-sans text-muted-foreground">{c.body}</pre>
        </div>
      ))}
    </div>
  );
}

function ClauseDiffBlock({ diff }: { diff: ClauseDiffEntry[] }) {
  const changes = diff.filter((d) => d.kind !== "unchanged");
  if (changes.length === 0) {
    return <p className="text-xs italic text-muted-foreground">No clause changes vs. previous round.</p>;
  }
  return (
    <div className="space-y-3">
      {changes.map((entry, idx) => {
        if (entry.kind === "added") {
          return (
            <div key={idx} className="border rounded-md p-3 bg-green-50/40">
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-green-600 hover:bg-green-600 text-xs">Added</Badge>
                <span className="text-sm font-medium">{entry.current.title}</span>
              </div>
              <pre className="text-xs whitespace-pre-wrap font-sans">{entry.current.body}</pre>
            </div>
          );
        }
        if (entry.kind === "removed") {
          return (
            <div key={idx} className="border rounded-md p-3 bg-red-50/40">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="destructive" className="text-xs">Removed</Badge>
                <span className="text-sm font-medium">{entry.previous.title}</span>
              </div>
              <pre className="text-xs whitespace-pre-wrap font-sans">{entry.previous.body}</pre>
            </div>
          );
        }
        return (
          <div key={idx} className="border rounded-md p-3">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="secondary" className="text-xs">Changed</Badge>
              <span className="text-sm font-medium">{entry.current.title}</span>
            </div>
            <LineDiff before={entry.previous.body} after={entry.current.body} />
          </div>
        );
      })}
    </div>
  );
}

interface RoundCardProps {
  round: NegotiationRound;
  previousRound: NegotiationRound | null;
  isLast: boolean;
  /** Task #561 — true when this is rounds[0] (the most recent round). Drives the "LATEST VERSION" ribbon. */
  isLatest: boolean;
  isAdmin: boolean;
  /** Task #563 — contract-scoped comments authored within this round's lifetime (createdAt boundary). */
  roundComments: ContractComment[];
  onReceive: (round: NegotiationRound) => void;
  onRevoke: (round: NegotiationRound) => void;
  onRestore: (round: NegotiationRound) => void;
  onResendEmail: (round: NegotiationRound) => void;
  onSendReminder: (round: NegotiationRound) => void;
  onDownloadReturned: (round: NegotiationRound) => void;
  isResending: boolean;
  isSendingReminder: boolean;
}

const MANUAL_REMINDER_COOLDOWN_MS = 24 * 60 * 60 * 1000;

function isFutureResponseDate(responseByDate: string | null | undefined): boolean {
  if (!responseByDate) return false;
  const parsed = new Date(responseByDate);
  if (Number.isNaN(parsed.getTime())) return false;
  const deadlineUtc = Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
  const now = new Date();
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return deadlineUtc > todayUtc;
}

function reminderCooldownMs(round: NegotiationRound): number {
  if (!round.lastManualReminderAt) return 0;
  const last = new Date(round.lastManualReminderAt).getTime();
  if (Number.isNaN(last)) return 0;
  const remaining = MANUAL_REMINDER_COOLDOWN_MS - (Date.now() - last);
  return Math.max(0, remaining);
}

function formatCooldown(ms: number): string {
  const totalMinutes = Math.ceil(ms / 60000);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

function shouldOfferManualReminder(round: NegotiationRound): boolean {
  if (round.direction !== "sent") return false;
  if (round.roundStatus === "received") return false;
  if (round.shareTokenRevokedAt) return false;
  if (!round.recipientEmail) return false;
  if (!isFutureResponseDate(round.responseByDate)) return false;
  return true;
}

function RoundCard({
  round,
  previousRound,
  isLast,
  isLatest,
  isAdmin,
  roundComments,
  onReceive,
  onRevoke,
  onRestore,
  onResendEmail,
  onSendReminder,
  onDownloadReturned,
  isResending,
  isSendingReminder,
}: RoundCardProps) {
  const isSent = round.direction === "sent";
  const isReceivedStatus = round.roundStatus === "received";
  const isRevoked = !!round.shareTokenRevokedAt;
  const Icon = isSent ? Send : ArrowLeftRight;
  const party = round.recipientName ?? "Unknown";
  const roleLabel = round.recipientRole ? ROLE_LABELS[round.recipientRole] ?? round.recipientRole : "";

  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState<"snapshot" | "diff">("snapshot");

  const snapshot = round.snapshotData;
  const hasFullSnapshot = !!(snapshot && (snapshot.clauses || snapshot.terms));
  const diff = useMemo(
    () => diffClauses(snapshot?.clauses, previousRound?.snapshotData?.clauses ?? []),
    [snapshot, previousRound],
  );
  const canDiff = !!previousRound && !!previousRound.snapshotData?.clauses;

  return (
    <div
      className={cn(
        "flex gap-4",
        // Task #561 — Subtle highlight rail on the latest round so the eye lands here first.
        isLatest && "relative rounded-md bg-gradient-to-r from-indigo-50/60 via-transparent to-transparent pl-2 -ml-2",
      )}
      data-testid={isLatest ? "round-card-latest" : `round-card-${round.roundNumber}`}
    >
      <div className="flex flex-col items-center">
        <div
          className={cn(
            "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2",
            isSent
              ? isReceivedStatus
                ? "bg-green-50 border-green-300 text-green-600"
                : "bg-orange-50 border-orange-300 text-orange-600"
              : "bg-blue-50 border-blue-300 text-blue-600",
            // Task #561 — Indigo ring on the latest round's icon — visual anchor for the demo.
            isLatest && "ring-2 ring-indigo-400 ring-offset-2 ring-offset-background shadow-sm",
          )}
        >
          {isReceivedStatus && isSent ? <MailCheck className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
        </div>
        {!isLast && <div className="w-px flex-1 bg-border mt-1" />}
      </div>

      <div className="pb-6 flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">
              {isSent ? "Sent to" : "Counter from"} {party}
            </span>
            {/* Task #561 — LATEST VERSION ribbon. Headline anchor: "this is the live version". */}
            {isLatest && (
              <Badge
                className="text-[10px] font-semibold uppercase tracking-wide bg-indigo-600 hover:bg-indigo-600 text-white border-transparent flex items-center gap-1 px-1.5 py-0.5"
                data-testid="badge-latest-version"
              >
                <Sparkles className="h-3 w-3" />
                Latest version
              </Badge>
            )}
            {roleLabel && (
              <Badge variant="outline" className="text-xs">{roleLabel}</Badge>
            )}
            {isSent && isReceivedStatus && (
              <Badge variant="outline" className="text-xs bg-green-50 border-green-300 text-green-700">Response received</Badge>
            )}
            {isSent && !isReceivedStatus && !isRevoked && (
              <Badge variant="outline" className="text-xs bg-orange-50 border-orange-300 text-orange-700">Awaiting response</Badge>
            )}
            {isRevoked && (
              <Badge variant="outline" className="text-xs bg-red-50 border-red-300 text-red-700">Link revoked</Badge>
            )}
            {isSent && <EmailStatusBadge round={round} />}
          </div>
          <div className="text-xs text-muted-foreground whitespace-nowrap">
            Round {round.roundNumber} · {format(new Date(round.createdAt), "MMM d, yyyy 'at' h:mm a")}
          </div>
        </div>

        {round.createdByName && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Logged by {round.createdByName}
          </p>
        )}

        {snapshot && (
          <div className="mt-2 flex flex-wrap gap-3">
            {snapshot.annualSalary != null && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <DollarSign className="h-3 w-3" />
                <span>Salary: {fmt(snapshot.annualSalary)}/yr</span>
              </div>
            )}
            {snapshot.clauseCount != null && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <FileText className="h-3 w-3" />
                <span>{snapshot.clauseCount} clause{snapshot.clauseCount !== 1 ? "s" : ""}</span>
              </div>
            )}
          </div>
        )}

        {round.responseByDate && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>Response by: <span className="font-medium">{round.responseByDate}</span></span>
          </div>
        )}

        {round.remindersSent && (round.remindersSent.day3 || round.remindersSent.day0) && (
          <div className="mt-2 space-y-1" data-testid={`reminders-sent-${round.id}`}>
            {round.remindersSent.day3 && (
              <div className="flex items-center gap-1.5 text-xs text-amber-700">
                <BellRing className="h-3 w-3" />
                <span>
                  3-day reminder sent{" "}
                  {format(new Date(round.remindersSent.day3), "MMM d, yyyy 'at' h:mm a")}
                </span>
              </div>
            )}
            {round.remindersSent.day0 && (
              <div className="flex items-center gap-1.5 text-xs text-red-700">
                <BellRing className="h-3 w-3" />
                <span>
                  Deadline-day reminder sent{" "}
                  {format(new Date(round.remindersSent.day0), "MMM d, yyyy 'at' h:mm a")}
                </span>
              </div>
            )}
          </div>
        )}

        {(round.manualReminderCount ?? 0) > 0 && round.lastManualReminderAt && (
          <div
            className="mt-2 flex items-center gap-1.5 text-xs text-blue-700"
            data-testid={`manual-reminder-sent-${round.id}`}
          >
            <BellRing className="h-3 w-3" />
            <span>
              Manual reminder sent{" "}
              {format(new Date(round.lastManualReminderAt), "MMM d, yyyy 'at' h:mm a")}
              {(round.manualReminderCount ?? 0) > 1 && (
                <> · {round.manualReminderCount} total</>
              )}
            </span>
          </div>
        )}

        {round.note && (
          <p className="mt-2 text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-2 border border-border">
            {round.note}
          </p>
        )}

        {round.coverNote && (
          <div className="mt-2">
            <p className="text-xs text-muted-foreground font-medium mb-1">Cover note:</p>
            <p className="text-sm text-muted-foreground bg-blue-50/50 rounded-md px-3 py-2 border border-blue-100">
              {round.coverNote}
            </p>
          </div>
        )}

        {round.receivedAt && round.counterpartyComments && (
          <div className="mt-2">
            <p className="text-xs text-muted-foreground font-medium mb-1 flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              Counterparty notes (received {format(new Date(round.receivedAt), "MMM d, yyyy")}):
            </p>
            <p className="text-sm text-muted-foreground bg-green-50/50 rounded-md px-3 py-2 border border-green-100">
              {round.counterpartyComments}
            </p>
          </div>
        )}

        {/*
         * Admin-only action row. Server enforces requireRole("admin") on all four
         * endpoints (receive, send-reminder, resend-email, revoke-link) in
         * artifacts/api-server/src/routes/negotiation.ts — this UI gate is
         * defense-in-depth and prevents viewers from seeing buttons that would
         * 403 on click. Do not remove without first removing the viewer role.
         */}
        {isAdmin && isSent && !isReceivedStatus && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => onReceive(round)}
            >
              <MailCheck className="h-3 w-3 mr-1.5 text-green-600" />
              Mark Received
            </Button>
            {shouldOfferManualReminder(round) && (() => {
              const cooldownMs = reminderCooldownMs(round);
              const onCooldown = cooldownMs > 0;
              const title = onCooldown
                ? `Another manual reminder can be sent in ${formatCooldown(cooldownMs)}.`
                : "Send the same branded reminder email to the counterparty now.";
              return (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => onSendReminder(round)}
                  disabled={isSendingReminder || onCooldown}
                  data-testid={`button-send-reminder-${round.id}`}
                  title={title}
                >
                  <BellRing className={cn("h-3 w-3 mr-1.5", isSendingReminder && "animate-pulse")} />
                  {isSendingReminder
                    ? "Sending..."
                    : onCooldown
                      ? `Reminder available in ${formatCooldown(cooldownMs)}`
                      : "Send reminder now"}
                </Button>
              );
            })()}
            {!isRevoked && shouldOfferResend(round) && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => onResendEmail(round)}
                disabled={isResending}
                data-testid={`button-resend-email-${round.id}`}
              >
                <RefreshCw className={cn("h-3 w-3 mr-1.5", isResending && "animate-spin")} />
                {isResending ? "Resending..." : "Resend Email"}
              </Button>
            )}
            {!isRevoked && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs text-red-600 hover:text-red-700"
                onClick={() => onRevoke(round)}
              >
                <Link2Off className="h-3 w-3 mr-1.5" />
                Revoke Link
              </Button>
            )}
          </div>
        )}

        {round.returnedDocumentKey && (() => {
          const downloadable = round.returnedDocumentScanStatus === "clean";
          return (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <DemoExportGate label="Download returned document" size="sm" className="h-7 text-xs">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => onDownloadReturned(round)}
                  disabled={!downloadable}
                  title={
                    downloadable
                      ? undefined
                      : "Download is blocked until the file passes a malware scan."
                  }
                  data-testid={`button-download-returned-${round.id}`}
                >
                  <Download className="h-3 w-3 mr-1.5" />
                  Download returned document
                </Button>
              </DemoExportGate>
              <ScanStatusBadge round={round} />
              {round.returnedDocumentFilename && (
                <span
                  className="text-xs text-muted-foreground inline-flex items-center gap-1 min-w-0"
                  title={round.returnedDocumentFilename}
                  data-testid={`text-returned-filename-${round.id}`}
                >
                  <FileText className="h-3 w-3 shrink-0" />
                  <span className="truncate max-w-[16rem]">{round.returnedDocumentFilename}</span>
                  {round.returnedDocumentSize != null && (
                    <span className="opacity-70 shrink-0">
                      · {formatFileSize(round.returnedDocumentSize)}
                    </span>
                  )}
                </span>
              )}
            </div>
          );
        })()}

        {round.emailLastError && (
          <p className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1">
            Last email error: {round.emailLastError}
          </p>
        )}

        {hasFullSnapshot && (
          <div className="mt-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`button-expand-round-${round.id}`}
            >
              {expanded ? <ChevronDown className="h-3 w-3 mr-1" /> : <ChevronRight className="h-3 w-3 mr-1" />}
              {expanded ? "Hide snapshot" : "View full snapshot"}
            </Button>

            {expanded && (
              <div className="mt-2 border rounded-md p-3 bg-muted/30 space-y-3">
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={view === "snapshot" ? "default" : "outline"}
                    className="h-7 text-xs"
                    onClick={() => setView("snapshot")}
                    data-testid={`button-view-snapshot-${round.id}`}
                  >
                    <FileText className="h-3 w-3 mr-1" />Snapshot
                  </Button>
                  <Button
                    size="sm"
                    variant={view === "diff" ? "default" : "outline"}
                    className="h-7 text-xs"
                    onClick={() => setView("diff")}
                    disabled={!canDiff}
                    data-testid={`button-view-diff-${round.id}`}
                  >
                    <GitCompare className="h-3 w-3 mr-1" />
                    Diff vs. previous
                  </Button>
                  {snapshot?.capturedAt && (
                    <span className="text-[10px] text-muted-foreground ml-auto">
                      Captured {format(new Date(snapshot.capturedAt), "MMM d, h:mm a")}
                    </span>
                  )}
                </div>

                {isAdmin && (
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => onRestore(round)}
                      data-testid={`button-restore-round-${round.id}`}
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Restore this version
                    </Button>
                  </div>
                )}

                {view === "snapshot" ? (
                  <>
                    {snapshot?.terms && (
                      <div>
                        <div className="text-xs font-semibold mb-1">Financial terms</div>
                        <TermsBlock terms={snapshot.terms} />
                      </div>
                    )}
                    <div>
                      <div className="text-xs font-semibold mb-1">Clauses</div>
                      <ClauseListBlock clauses={snapshot?.clauses ?? []} />
                    </div>
                  </>
                ) : (
                  <ClauseDiffBlock diff={diff} />
                )}
              </div>
            )}
          </div>
        )}

        {/* Task #563 — Inline counterparty + internal comments tied to this round.
            Comments are filtered upstream by createdAt boundary (this round’s createdAt
            → next round’s createdAt). Always visible so the demo shows the back-and-forth
            conversation without the user having to open the comments tab. */}
        {roundComments.length > 0 && (
          <div
            className="mt-3 rounded-md border border-indigo-100 bg-indigo-50/40 p-3"
            data-testid={`round-comments-${round.id}`}
          >
            <div className="flex items-center gap-1.5 mb-2">
              <MessageSquare className="h-3.5 w-3.5 text-indigo-600" />
              <span className="text-xs font-semibold text-indigo-900">
                Notes during this round
              </span>
              <Badge variant="outline" className="text-[10px] h-4 px-1.5 bg-white border-indigo-200 text-indigo-700">
                {roundComments.length}
              </Badge>
            </div>
            <ul className="space-y-2">
              {roundComments.map((c) => (
                <li key={c.id} className="text-xs" data-testid={`round-comment-${c.id}`}>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-medium text-foreground">{c.authorName}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {format(new Date(c.createdAt), "MMM d, h:mm a")}
                    </span>
                  </div>
                  <p className="text-foreground/80 whitespace-pre-wrap mt-0.5">{c.body}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

interface NegotiationRoundsTimelineProps {
  contractId: number;
  refreshTrigger?: number;
  isAdmin?: boolean;
  /**
   * ISO timestamp when delivery-failure alerts were muted on this contract,
   * or null when alerts are active. Passed in by the parent (it already has
   * the contract loaded) so the toggle can render the correct state on
   * first paint without a second roundtrip.
   */
  deliveryAlertsMutedAt?: string | null;
  /** Called after the toggle succeeds so the parent can refetch the contract. */
  onDeliveryAlertsMutedChange?: () => void;
  /**
   * Task #562 — Contract lifecycle state, used to render the pipeline status pill
   * in the timeline header ("Open with counterparty" / "Counter received" / "Locked" / etc.).
   * Parent already has the contract loaded so we accept it as a prop instead of re-fetching.
   */
  lifecycleState?: string | null;
  /**
   * Task #562 — Who currently holds the pen ("us" | "counterparty"). Combined with
   * lifecycleState to produce a single, demo-friendly status pill.
   */
  negotiationHolder?: string | null;
}

export function NegotiationRoundsTimeline({
  contractId,
  refreshTrigger,
  isAdmin = false,
  deliveryAlertsMutedAt = null,
  onDeliveryAlertsMutedChange,
  lifecycleState = null,
  negotiationHolder = null,
}: NegotiationRoundsTimelineProps) {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [rounds, setRounds] = useState<NegotiationRound[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Task #563 — Contract-scoped comments for inline per-round display.
  // Fetched once per contractId/refreshTrigger and grouped by createdAt boundary below.
  const [comments, setComments] = useState<ContractComment[]>([]);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [selectedRound, setSelectedRound] = useState<NegotiationRound | null>(null);
  const [restoreRound, setRestoreRound] = useState<NegotiationRound | null>(null);
  const [restoring, setRestoring] = useState(false);
  // Local optimistic copy of the mute state. Initialised from the prop and
  // kept in sync via the effect below so an external refetch (e.g. parent
  // invalidates the contract query) is reflected immediately.
  const [mutedAt, setMutedAt] = useState<string | null>(deliveryAlertsMutedAt);
  const [isTogglingMute, setIsTogglingMute] = useState(false);
  useEffect(() => {
    setMutedAt(deliveryAlertsMutedAt);
  }, [deliveryAlertsMutedAt]);

  const handleToggleMute = useCallback(async () => {
    const nextMuted = mutedAt == null;
    setIsTogglingMute(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `/api/contracts/${contractId}/delivery-alerts-mute`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ muted: nextMuted }),
        },
      );
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? "Failed to update mute state");
      }
      const data = (await res.json()) as {
        deliveryAlertsMutedAt: string | null;
      };
      setMutedAt(data.deliveryAlertsMutedAt);
      toast({
        title: nextMuted ? "Delivery alerts muted" : "Delivery alerts unmuted",
        description: nextMuted
          ? "Bounce, spam, and delivery-failure notifications are now silenced for this contract."
          : "You'll receive in-app alerts again when an email to this contract bounces or fails.",
      });
      onDeliveryAlertsMutedChange?.();
    } catch (err) {
      toast({
        title: "Couldn't update alerts",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsTogglingMute(false);
    }
  }, [contractId, getToken, mutedAt, onDeliveryAlertsMutedChange, toast]);

  const fetchRounds = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/contracts/${contractId}/negotiation-rounds`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRounds(data);
      }
    } catch { /* ignore */ }
    setIsLoading(false);
  }, [contractId, getToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const token = await getToken();
        const res = await fetch(`/api/contracts/${contractId}/negotiation-rounds`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setRounds(data);
        }
      } catch { /* ignore */ }
      if (!cancelled) setIsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [contractId, getToken, refreshTrigger]);

  // Task #563 — Fetch contract-scoped comments alongside rounds.
  // Failure here is non-fatal: we silently render zero comment panels rather
  // than block the timeline. The comments endpoint returns asc-ordered rows.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(
          `/api/comments?entityType=contract&entityId=${contractId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.ok && !cancelled) {
          const data = (await res.json()) as ContractComment[];
          setComments(Array.isArray(data) ? data : []);
        }
      } catch { /* non-fatal — timeline still renders without comments */ }
    })();
    return () => { cancelled = true; };
  }, [contractId, getToken, refreshTrigger]);

  const handleReceive = (round: NegotiationRound) => {
    setSelectedRound(round);
    setReceiveDialogOpen(true);
  };

  const [resendingRoundId, setResendingRoundId] = useState<number | null>(null);
  const [sendingReminderRoundId, setSendingReminderRoundId] = useState<number | null>(null);
  const [reminderRound, setReminderRound] = useState<NegotiationRound | null>(null);
  const [reminderNote, setReminderNote] = useState("");
  // Task #562: native confirm() blocks the event loop, can't be styled,
  // and breaks the design system. Replace with AlertDialog (same pattern
  // already used for Send Reminder and Restore). Two new dialog states:
  const [resendRound, setResendRound] = useState<NegotiationRound | null>(null);
  const [revokeRound, setRevokeRound] = useState<NegotiationRound | null>(null);
  const [revokingRoundId, setRevokingRoundId] = useState<number | null>(null);

  const REMINDER_NOTE_MAX = 500;

  const handleSendReminder = (round: NegotiationRound) => {
    if (!round.recipientEmail) {
      toast({
        title: "No recipient email",
        description: "This round was sent without an email address.",
        variant: "destructive",
      });
      return;
    }
    setReminderNote("");
    setReminderRound(round);
  };

  const handleConfirmSendReminder = async () => {
    const round = reminderRound;
    if (!round) return;
    const note = reminderNote.trim();
    if (note.length > REMINDER_NOTE_MAX) return;
    setSendingReminderRoundId(round.id);
    try {
      const token = await getToken();
      const res = await fetch(
        `/api/contracts/${contractId}/negotiation-rounds/${round.id}/send-reminder`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(note.length > 0 ? { message: note } : {}),
        },
      );
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as {
          error?: string;
          message?: string;
        };
        throw new Error(err.message ?? err.error ?? "Failed to send reminder");
      }
      toast({
        title: "Reminder sent",
        description: note.length > 0
          ? `Reminder with your note sent to ${round.recipientEmail}.`
          : `Reminder email sent to ${round.recipientEmail}.`,
      });
      setReminderRound(null);
      setReminderNote("");
      fetchRounds();
    } catch (err) {
      toast({
        title: "Could not send reminder",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setSendingReminderRoundId(null);
    }
  };

  // Public entrypoint from the row "Resend Email" button. Opens the
  // AlertDialog instead of firing native confirm() inline so the user
  // gets a styled, accessible confirmation step.
  const handleResendEmail = (round: NegotiationRound) => {
    if (!round.recipientEmail) {
      toast({ title: "No recipient email", description: "This round was sent without an email address.", variant: "destructive" });
      return;
    }
    setResendRound(round);
  };

  // Actual network call — invoked from the AlertDialog confirm action.
  const handleConfirmResend = async () => {
    const round = resendRound;
    if (!round) return;
    setResendingRoundId(round.id);
    try {
      const token = await getToken();
      const res = await fetch(
        `/api/contracts/${contractId}/negotiation-rounds/${round.id}/resend-email`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Failed to resend email");
      }
      toast({ title: "Email resent", description: `Negotiation email sent again to ${round.recipientEmail}.` });
      fetchRounds();
      setResendRound(null);
    } catch (err) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not resend email.", variant: "destructive" });
    } finally {
      setResendingRoundId(null);
    }
  };

  const handleRestoreConfirm = async () => {
    if (!restoreRound) return;
    setRestoring(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `/api/contracts/${contractId}/negotiation-rounds/${restoreRound.id}/restore`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string; error?: string }).message
          ?? (err as { error?: string }).error
          ?? "Failed to restore version");
      }
      toast({
        title: "Version restored",
        description: `Contract clauses and financial terms restored from Round ${restoreRound.roundNumber}.`,
      });
      setRestoreRound(null);
      fetchRounds();
    } catch (err) {
      toast({
        title: "Could not restore",
        description: err instanceof Error ? err.message : "An unknown error occurred.",
        variant: "destructive",
      });
    } finally {
      setRestoring(false);
    }
  };

  const handleDownloadReturned = async (round: NegotiationRound) => {
    if (!round.returnedDocumentKey) return;
    try {
      const token = await getToken();
      const res = await fetch(`/api/contracts/${contractId}/negotiation-rounds/${round.id}/returned-document`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 403) {
          const body = (await res.json().catch(() => ({}))) as {
            error?: string;
            code?: string;
            message?: string;
            scanStatus?: string;
          };
          if (body.code === "malware_scan_not_clean" || body.error === "malware_scan_not_clean") {
            const isInfected = body.scanStatus === "infected";
            toast({
              title: isInfected
                ? "Download blocked — malware detected"
                : "Download blocked — file not verified",
              description:
                body.message ??
                (isInfected
                  ? "This document was flagged by malware scanning. Ask the counterparty to resend a clean copy."
                  : "This document hasn't been verified by malware scanning. Ask the counterparty to resend so we can re-scan it."),
              variant: "destructive",
            });
            fetchRounds();
            return;
          }
        }
        throw new Error("Download failed");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Prefer the original uploaded filename so the saved file keeps its
      // extension and the counterparty's naming. Fall back to a filename
      // parsed from the response Content-Disposition (which the server now
      // sets via RFC 5987), and finally to a generic "document" label.
      a.download =
        round.returnedDocumentFilename
        ?? parseFilenameFromContentDisposition(res.headers.get("Content-Disposition"))
        ?? "document";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      toast({ title: "Error", description: "Failed to download document", variant: "destructive" });
    }
  };

  // Public entrypoint from the row "Revoke Link" button. Opens the
  // confirmation dialog instead of firing native confirm() inline.
  const handleRevoke = (round: NegotiationRound) => {
    setRevokeRound(round);
  };

  // Actual network call — invoked from the AlertDialog confirm action.
  const handleConfirmRevoke = async () => {
    const round = revokeRound;
    if (!round) return;
    setRevokingRoundId(round.id);
    try {
      const token = await getToken();
      const res = await fetch(`/api/contracts/${contractId}/negotiation-rounds/${round.id}/revoke-link`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Failed to revoke link");
      }
      toast({ title: "Link revoked", description: "The share link has been revoked." });
      fetchRounds();
      setRevokeRound(null);
    } catch (err) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not revoke link.", variant: "destructive" });
    } finally {
      setRevokingRoundId(null);
    }
  };

  // For diffing: rounds come back in desc order. Each round's "previous" round
  // is the one with roundNumber - 1 chronologically.
  const roundsByNumber = useMemo(() => {
    const map = new Map<number, NegotiationRound>();
    for (const r of rounds) map.set(r.roundNumber, r);
    return map;
  }, [rounds]);

  // Task #563 — Group comments into per-round buckets by createdAt boundary.
  // A comment belongs to round R if it was created between R.createdAt and
  // the next chronological round's createdAt (or now, if R is the latest).
  // Comments created before the first round are bucketed onto the oldest round
  // so nothing is silently dropped during the demo.
  const commentsByRoundId = useMemo(() => {
    const map = new Map<number, ContractComment[]>();
    if (rounds.length === 0) return map;

    // Rounds asc by createdAt for boundary math (the source list is desc).
    const roundsAsc = [...rounds].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    for (const r of roundsAsc) map.set(r.id, []);

    const boundaries = roundsAsc.map((r) => new Date(r.createdAt).getTime());
    for (const c of comments) {
      const t = new Date(c.createdAt).getTime();
      // Find the latest round whose createdAt is <= comment.createdAt.
      // If none (comment predates round 1), bucket on round 1 so it still surfaces.
      let idx = -1;
      for (let i = 0; i < boundaries.length; i++) {
        if (boundaries[i] <= t) idx = i;
        else break;
      }
      if (idx === -1) idx = 0;
      const bucket = map.get(roundsAsc[idx].id);
      if (bucket) bucket.push(c);
    }
    return map;
  }, [rounds, comments]);

  // Task #562 — Single, demo-friendly pipeline status pill.
  // Synthesises lifecycleState + negotiationHolder + latest-round receipt state
  // into one label + icon + color the user (and an Athletic Director in the room)
  // can read in under a second. Falls back to neutral when nothing is known.
  const pipelineStatus = useMemo(() => {
    const latest = rounds[0] ?? null; // rounds are desc-ordered
    const state = (lifecycleState ?? "").toLowerCase();

    if (state === "locked" || state === "finalized" || state === "signed") {
      return {
        label: "Locked · awaiting signature",
        icon: Lock,
        className: "bg-slate-900 text-white border-transparent hover:bg-slate-900",
      };
    }
    if (state === "legal_review") {
      return {
        label: "In legal review",
        icon: ShieldCheck,
        className: "bg-purple-600 text-white border-transparent hover:bg-purple-600",
      };
    }
    if (state === "counter_received" || (latest && latest.direction === "received")) {
      return {
        label: "Counter received · our move",
        icon: Inbox,
        className: "bg-blue-600 text-white border-transparent hover:bg-blue-600",
      };
    }
    if (state === "out_for_negotiation" || negotiationHolder === "counterparty") {
      const party = latest?.recipientName ? ` with ${latest.recipientName}` : " with counterparty";
      return {
        label: `Open${party}`,
        icon: Hourglass,
        className: "bg-amber-500 text-white border-transparent hover:bg-amber-500",
      };
    }
    if (state === "executed" || state === "active") {
      return {
        label: "Executed",
        icon: CheckCircle2,
        className: "bg-emerald-600 text-white border-transparent hover:bg-emerald-600",
      };
    }
    // Default — draft / unknown / negotiating but no holder hint.
    return null;
  }, [rounds, lifecycleState, negotiationHolder]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-sm">Negotiation Rounds</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {[1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
        </CardContent>
      </Card>
    );
  }

  if (rounds.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-sm">Negotiation Rounds</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground italic">
            No negotiation rounds yet. Use "Send to Coach / Representation" to start the external negotiation cycle.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-sm">Negotiation Rounds</CardTitle>
              {/* Task #562 — Pipeline status pill: one-glance signal of where this contract is. */}
              {pipelineStatus && (() => {
                const PipelineIcon = pipelineStatus.icon;
                return (
                  <Badge
                    className={cn("text-xs flex items-center gap-1 px-2 py-0.5", pipelineStatus.className)}
                    data-testid="badge-negotiation-pipeline-status"
                  >
                    <PipelineIcon className="h-3 w-3" />
                    {pipelineStatus.label}
                  </Badge>
                );
              })()}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {mutedAt != null && (
                <Badge
                  variant="outline"
                  className="text-xs bg-amber-50 border-amber-300 text-amber-700 flex items-center gap-1"
                  title={`Delivery alerts muted since ${format(new Date(mutedAt), "MMM d, yyyy 'at' h:mm a")}`}
                  data-testid="badge-delivery-alerts-muted"
                >
                  <BellOff className="h-3 w-3" />
                  Delivery alerts muted
                </Badge>
              )}
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={handleToggleMute}
                disabled={isTogglingMute}
                data-testid="button-toggle-delivery-alerts-mute"
                title={
                  mutedAt != null
                    ? "Re-enable bounce, spam, and delivery-failure notifications for this contract."
                    : "Silence bounce, spam, and delivery-failure notifications for this contract."
                }
              >
                {mutedAt != null ? (
                  <>
                    <Bell className="h-3 w-3 mr-1.5" />
                    {isTogglingMute ? "Updating…" : "Unmute delivery alerts"}
                  </>
                ) : (
                  <>
                    <BellOff className="h-3 w-3 mr-1.5" />
                    {isTogglingMute ? "Updating…" : "Mute delivery alerts"}
                  </>
                )}
              </Button>
              <Badge variant="outline" className="text-xs">{rounds.length} round{rounds.length !== 1 ? "s" : ""}</Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-0">
            {rounds.map((round, idx) => (
              <RoundCard
                key={round.id}
                round={round}
                previousRound={roundsByNumber.get(round.roundNumber - 1) ?? null}
                isLast={idx === rounds.length - 1}
                // Task #561 — rounds are desc-ordered, so idx === 0 is the newest round.
                isLatest={idx === 0}
                isAdmin={isAdmin}
                // Task #563 — Per-round comments slice (empty array when none).
                roundComments={commentsByRoundId.get(round.id) ?? []}
                onReceive={handleReceive}
                onRevoke={handleRevoke}
                onRestore={(r) => setRestoreRound(r)}
                onResendEmail={handleResendEmail}
                onSendReminder={handleSendReminder}
                onDownloadReturned={handleDownloadReturned}
                isResending={resendingRoundId === round.id}
                isSendingReminder={sendingReminderRoundId === round.id}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {selectedRound && (
        <ReceiveResponseDialog
          open={receiveDialogOpen}
          onOpenChange={setReceiveDialogOpen}
          contractId={contractId}
          roundId={selectedRound.id}
          roundNumber={selectedRound.roundNumber}
          recipientName={selectedRound.recipientName}
          onSuccess={fetchRounds}
        />
      )}

      <AlertDialog
        open={!!reminderRound}
        onOpenChange={(open) => {
          if (!open && sendingReminderRoundId === null) {
            setReminderRound(null);
            setReminderNote("");
          }
        }}
      >
        <AlertDialogContent data-testid="dialog-send-reminder">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Send reminder for Round {reminderRound?.roundNumber}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              We'll send the standard branded reminder email to{" "}
              <strong>{reminderRound?.recipientEmail}</strong>. You can add an
              optional short note that will appear near the top of the email.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reminder-note" className="text-xs font-medium">
              Optional note (visible to recipient)
            </Label>
            <Textarea
              id="reminder-note"
              data-testid="input-reminder-note"
              value={reminderNote}
              onChange={(e) => setReminderNote(e.target.value)}
              placeholder="e.g. Just checking in — let me know if you need an extension."
              rows={4}
              maxLength={REMINDER_NOTE_MAX}
              disabled={sendingReminderRoundId !== null}
            />
            <p className="text-xs text-muted-foreground text-right">
              {reminderNote.trim().length}/{REMINDER_NOTE_MAX}
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sendingReminderRoundId !== null}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmSendReminder();
              }}
              disabled={sendingReminderRoundId !== null}
              data-testid="button-confirm-send-reminder"
            >
              {sendingReminderRoundId !== null ? "Sending…" : "Send reminder"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!restoreRound}
        onOpenChange={(open) => { if (!open && !restoring) setRestoreRound(null); }}
      >
        <AlertDialogContent data-testid="dialog-restore-round">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore Round {restoreRound?.roundNumber}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will overwrite the contract's current clauses and financial terms with the
              snapshot captured at Round {restoreRound?.roundNumber}
              {restoreRound?.snapshotData?.capturedAt
                ? ` on ${format(new Date(restoreRound.snapshotData.capturedAt), "MMM d, yyyy 'at' h:mm a")}`
                : ""}.
              {" "}Any clauses added since then will be removed, removed clauses will be
              re-attached, and current overrides will be replaced. Locked clauses must be
              unlocked first. This action is recorded in the audit log but cannot be undone
              automatically — restore a later round to reverse it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoring}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); void handleRestoreConfirm(); }}
              disabled={restoring}
              data-testid="button-confirm-restore"
            >
              {restoring ? "Restoring…" : "Restore version"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Task #562: Resend-email confirmation dialog — replaces native confirm(). */}
      <AlertDialog
        open={!!resendRound}
        onOpenChange={(open) => { if (!open && resendingRoundId === null) setResendRound(null); }}
      >
        <AlertDialogContent data-testid="dialog-resend-email">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Resend email for Round {resendRound?.roundNumber}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              We'll resend the original negotiation email to{" "}
              <strong>{resendRound?.recipientEmail}</strong>. The share link is
              the same as before — this is useful when the counterparty
              reports the email never arrived or hit spam.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resendingRoundId !== null}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); void handleConfirmResend(); }}
              disabled={resendingRoundId !== null}
              data-testid="button-confirm-resend-email"
            >
              {resendingRoundId !== null ? "Resending…" : "Resend email"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Task #562: Revoke-link confirmation dialog — replaces native confirm(). */}
      <AlertDialog
        open={!!revokeRound}
        onOpenChange={(open) => { if (!open && revokingRoundId === null) setRevokeRound(null); }}
      >
        <AlertDialogContent data-testid="dialog-revoke-link">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Revoke share link for Round {revokeRound?.roundNumber}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The counterparty
              {revokeRound?.recipientName ? <> (<strong>{revokeRound.recipientName}</strong>)</> : null}
              {" "}will no longer be able to access the guest contract page using
              the share link from this round. Their session is invalidated
              immediately. This is recorded in the audit log and cannot be
              undone — issue a new send if they need access again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revokingRoundId !== null}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); void handleConfirmRevoke(); }}
              disabled={revokingRoundId !== null}
              data-testid="button-confirm-revoke-link"
            >
              {revokingRoundId !== null ? "Revoking…" : "Revoke link"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
